import React, { useEffect, useState } from "react";
import { Activity, Dice5, TrendingDown, RefreshCw } from "lucide-react";
import api from "@/lib/api";

function Line({ data, color = "#06B6D4", h = 120, baseline }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data, baseline ?? Math.min(...data));
  const max = Math.max(...data, baseline ?? Math.max(...data));
  const rng = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * 100},${100 - ((v - min) / rng) * 100}`).join(" ");
  const by = baseline != null ? 100 - ((baseline - min) / rng) * 100 : null;
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ height: h }} className="w-full">
      {by != null && <line x1="0" y1={by} x2="100" y2={by} stroke="rgba(148,163,184,0.3)" strokeWidth="0.4" strokeDasharray="2" />}
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function EdgeLab() {
  const [wr, setWr] = useState(45), [aw, setAw] = useState(2), [al, setAl] = useState(1), [e, setE] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.post("/risk/expectancy", { win_rate: wr / 100, avg_win_r: +aw, avg_loss_r: +al }).then((r) => setE(r.data)).catch(() => {}), 150);
    return () => clearTimeout(t);
  }, [wr, aw, al]);
  const Slider = ({ label, val, set, min, max, step, unit }) => (
    <div>
      <div className="flex justify-between text-[12px] mb-1"><span className="text-tc-sub">{label}</span><span className="font-mono text-tc-txt">{val}{unit}</span></div>
      <input type="range" min={min} max={max} step={step} value={val} onChange={(ev) => set(+ev.target.value)} className="w-full accent-tc-coach" data-testid={`edge-${label.split(" ")[0].toLowerCase()}`} />
    </div>
  );
  return (
    <div className="tc-card p-5 space-y-4" data-testid="edge-lab">
      <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach">Edge Lab · Expectancy</div>
      <Slider label="Win Rate" val={wr} set={setWr} min={5} max={95} step={1} unit="%" />
      <Slider label="Avg Win (R)" val={aw} set={setAw} min={0.1} max={5} step={0.1} unit="R" />
      <Slider label="Avg Loss (R)" val={al} set={setAl} min={0.1} max={5} step={0.1} unit="R" />
      {e && (
        <div className={`rounded-lg p-4 text-center ${e.expectancy_r > 0 ? "bg-tc-bull/10" : e.expectancy_r < 0 ? "bg-tc-bear/10" : "bg-white/5"}`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted">Expectancy / Trade</div>
          <div className={`font-heading text-4xl font-extrabold ${e.expectancy_r > 0 ? "text-tc-bull" : e.expectancy_r < 0 ? "text-tc-bear" : "text-tc-sub"}`} data-testid="edge-result">{e.expectancy_r > 0 ? "+" : ""}{e.expectancy_r}R</div>
          <div className="text-[12px] text-tc-sub mt-1 uppercase">{e.verdict}</div>
        </div>
      )}
      <p className="text-[11px] text-tc-muted">Try to build a profitable system that wins only 35% of the time. It's possible — that's the point.</p>
    </div>
  );
}

function StreakSim() {
  const [d, setD] = useState(null), [seed, setSeed] = useState(42), [busy, setBusy] = useState(false);
  const run = (s) => { setBusy(true); api.post("/risk/streak-sim", { seed: s, trades: 50, win_rate: 0.45, avg_win_r: 2, avg_loss_r: 1 }).then((r) => setD(r.data)).finally(() => setBusy(false)); };
  useEffect(() => { run(seed); }, []);
  return (
    <div className="tc-card p-5 space-y-3" data-testid="streak-sim">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach">Streak Simulator · same edge, seeded</div>
        <button data-testid="streak-reroll" onClick={() => { const s = seed + 1; setSeed(s); run(s); }} className="text-[11px] font-mono px-2.5 py-1 rounded bg-white/5 hover:bg-white/10 flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> New seed</button>
      </div>
      {d && <>
        <Line data={d.equity} color="#06B6D4" baseline={10000} h={130} />
        <div className="grid grid-cols-4 gap-2 text-center text-[12px]">
          <div><div className="font-mono text-tc-txt" data-testid="streak-final">${d.final_balance}</div><div className="text-[9px] text-tc-muted uppercase">Balance</div></div>
          <div><div className="font-mono text-tc-txt">{d.realized_win_rate}%</div><div className="text-[9px] text-tc-muted uppercase">Realized WR</div></div>
          <div><div className="font-mono text-tc-txt">{d.total_r}R</div><div className="text-[9px] text-tc-muted uppercase">Total R</div></div>
          <div><div className="font-mono text-tc-bear">{d.max_drawdown}%</div><div className="text-[9px] text-tc-muted uppercase">Max DD</div></div>
        </div>
        <p className="text-[11px] text-tc-muted">Same {d.expected_win_rate}% edge, different seed → very different journey. Expectancy emerges over a sample; it never promises the next trade.</p>
      </>}
      {busy && <div className="text-tc-muted text-xs">Simulating…</div>}
    </div>
  );
}

function RuinLab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/risk/ruin?losses=10").then((r) => setD(r.data)).catch(() => {}); }, []);
  const COL = { 1: "#10B981", 2: "#06B6D4", 5: "#F59E0B", 10: "#F97316", 25: "#EF4444" };
  return (
    <div className="tc-card p-5 space-y-3" data-testid="ruin-lab">
      <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach">Risk of Ruin · 10 losses in a row</div>
      {d && <>
        <div className="relative">
          {d.trajectories.map((t) => <div key={t.risk_pct} className="absolute inset-0"><Line data={t.curve} color={COL[t.risk_pct]} baseline={10000} h={150} /></div>)}
          <div style={{ height: 150 }} />
        </div>
        <div className="grid sm:grid-cols-5 gap-2 text-center text-[11px]">
          {d.trajectories.map((t) => (
            <div key={t.risk_pct} className="rounded-lg bg-white/5 py-2" data-testid={`ruin-${t.risk_pct}`}>
              <div className="font-mono" style={{ color: COL[t.risk_pct] }}>{t.risk_pct}% risk</div>
              <div className="text-tc-txt font-mono">{t.pct_remaining}% left</div>
              <div className="text-[9px] text-tc-muted">need +{t.recovery_needed}%</div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-tc-muted">Big losses require much bigger gains to recover. Lose 50% → you need +100% just to get back.</p>
      </>}
    </div>
  );
}

export default function RiskLab() {
  const [tab, setTab] = useState("edge");
  const tabs = [["edge", "Edge Lab", Activity], ["streak", "Streak Sim", Dice5], ["ruin", "Risk of Ruin", TrendingDown]];
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto" data-testid="risk-lab">
      <h1 className="font-heading text-2xl font-bold">Risk Lab</h1>
      <p className="text-tc-sub text-sm mt-1">Experience the math of survival — expectancy, probability and recovery. Deterministic, illustrative only.</p>
      <div className="flex gap-2 mt-4 mb-4">
        {tabs.map(([k, l, Icon]) => (
          <button key={k} data-testid={`risklab-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-mono flex items-center gap-1.5 transition-colors ${tab === k ? "bg-tc-coach text-tc-bg" : "bg-white/5 text-tc-sub hover:text-tc-txt"}`}>
            <Icon className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>
      {tab === "edge" && <EdgeLab />}
      {tab === "streak" && <StreakSim />}
      {tab === "ruin" && <RuinLab />}
    </div>
  );
}
